import React, { useState, useEffect, useRef } from 'react';
import { MoreHorizontal } from 'lucide-react';

/**
 * SafetyMenu — small overflow ⋯ button that opens a popover with a list
 * of actions. Each action is a `{ key, label, onClick, danger?, testid }` item.
 */
const SafetyMenu = ({ items, t, isRTL, testid = 'safety-menu', size = 'sm', align = 'end' }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!items || items.length === 0) return null;

  const btnSize = size === 'sm' ? 'p-1.5' : 'p-2';
  const alignClass = align === 'start' ? (isRTL ? 'right-0' : 'left-0') : isRTL ? 'left-0' : 'right-0';

  return (
    <div ref={wrapRef} className="relative" data-testid={`${testid}-wrap`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        data-testid={testid}
        aria-label={t('moreActions')}
        className={`${btnSize} rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100`}
      >
        <MoreHorizontal className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} />
      </button>
      {open && (
        <div
          className={`absolute top-full mt-1 ${alignClass} w-52 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden z-40`}
          data-testid={`${testid}-popover`}
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              onClick={() => {
                setOpen(false);
                it.onClick && it.onClick();
              }}
              data-testid={it.testid || `${testid}-${it.key}`}
              className={`w-full text-start px-3 py-2.5 text-sm hover:bg-gray-50 ${
                it.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700'
              }`}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SafetyMenu;
